import { Button, Divider, Drawer, Fab, Grid, makeStyles, Typography } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { defer } from '@thalesrc/js-utils';
import React, { useCallback, useContext, useRef } from 'react';

import AutoComplete from 'Common/Form/AutoComplete';
import DateInput from 'Common/Form/DateInput';
import Form, { FormRef } from 'Common/Form/Form';
import { HSTableContext } from 'Common/Table/HSTable/hs-table.context';
import Toolbar from 'Common/Toolbar';
import { SelectOptionsContext } from 'context/select-options.context';
import { useAsyncEffect, useDialogState } from 'utils';

import { EmployeeSearchResult, getEmployeesByIds, searchEmployee } from '../../../../../api/hs/employee';
import { SelectOption } from '../../../../../Common/Form/Select';
import TextField from '../../../../../Common/Form/TextField';

const useStyles = makeStyles(() => ({
  fab: {
    transform: 'translateY(50%)',
  },
  drawer: {
    maxWidth: 320,
  },
}));

function mapEmployeeToOptions(types: EmployeeSearchResult[]): SelectOption<string>[] {
  return types.map<SelectOption<string>>(({ id, name }) => ({ text: name, value: id }));
}

async function employeeOptionFetcher(value: string) {
  const res = await searchEmployee(value);

  return mapEmployeeToOptions(res);
}

async function employeeLabelFetcher(values: string[]) {
  const res = await getEmployeesByIds(values);

  return mapEmployeeToOptions(res);
}

export default function Filters() {
  const classes = useStyles();

  const { open, close, opened } = useDialogState();
  const { setExtraArgs, extraArgs } = useContext(HSTableContext);
  const {
    companies,
    accidentTypes,
    shiftDefinitions,
    departments,
    workStations,
    accidentReviewStatus,
    employeeBodyRegions,
    accidentSeverityTypes,
  } = useContext(SelectOptionsContext);

  const formRef = useRef<FormRef<Record<string, any>>>();

  const submit = useCallback(
    values => {
      setExtraArgs({ filters: values });
      close();
    },
    [setExtraArgs, close]
  );

  const clear = useCallback(() => {
    setExtraArgs({ filters: {} });
    close();
  }, [setExtraArgs, close]);

  useAsyncEffect(async () => {
    if (!extraArgs.filters || !opened) {
      return;
    }

    await defer();

    for (const [key, value] of Object.entries(extraArgs.filters)) {
      formRef.current.methods.setValue(key, value);
    }
    // formRef?.current?.methods?.setValue(
    //   'employeeInjuryTypes',
    //   data?.employeeInjuryTypes?.map(employeeInjuryType => employeeInjuryType.injuryTypeDefinitionId)
    // );

    formRef.current.methods.trigger();
  }, [extraArgs, opened]);

  return (
    <>
      <Fab
        onClick={open}
        size="small"
        className={classes.fab}
        style={{
          backgroundColor: `${
            Object.keys(extraArgs)?.length > 0 ? (Object.keys(extraArgs?.filters)?.length > 0 ? '#f50057' : '#2196f3') : '#2196f3'
          }`,
          color: 'white',
        }}>
        <FilterListIcon />
      </Fab>
      <Drawer anchor="left" open={opened} onClose={close} classes={{ paper: classes.drawer }}>
        <Toolbar color="secondary" className="mb-2">
          <Typography variant="h6">Kaza Kaydı Filtreleme</Typography>
        </Toolbar>
        <Form onSubmit={submit} ref={formRef}>
          <Grid container className="p-2">
            <Grid item xs={12} md={12} sm={12} lg={12}>
              <AutoComplete name="employeeIds" label="Personel" optionFetcher={employeeOptionFetcher} labelFetcher={employeeLabelFetcher} />
            </Grid>
            <Grid item md={12} sm={12}>
              <AutoComplete name="companyIds" options={companies} label="Şirket" />
            </Grid>
            <Grid item md={12} sm={12}>
              <AutoComplete name="departmentIds" options={departments} label="Departman" />
            </Grid>

            <Grid item md={12} sm={12}>
              <AutoComplete name="stationIds" options={workStations} label="İstasyon" />
            </Grid>
            <Grid item md={12} sm={12}>
              <AutoComplete name="accidentTypeDefinitionIds" options={accidentTypes} label="Kaza Türü" />
            </Grid>

            <Grid item md={12} sm={12}>
              <AutoComplete name="bodyRegionDefinitionIds" options={employeeBodyRegions} label="Vücut Bölgeleri" />
            </Grid>
            <Grid item md={12} sm={12}>
              <AutoComplete name="severityDefinitionIds" options={accidentSeverityTypes} label="Kaza Şiddeti" />
            </Grid>

            <Grid item md={12} sm={12}>
              <AutoComplete single name="shiftId" options={shiftDefinitions} label="Vardiya" />
            </Grid>
            <Grid item md={12} sm={12}>
              <AutoComplete single name="accidentReviewStatus" options={accidentReviewStatus} label="Kaza İnceleme Durumu" />
            </Grid>

            <Grid spacing={1} item container xs={12} md={12} sm={12} lg={12}>
              <Grid item xs={12} md={12} sm={12} lg={12}>
                <Typography variant="subtitle1">Kaza Tarihi</Typography>
                <Divider />
              </Grid>
              <Grid item xs={6} md={6} sm={6} lg={6}>
                <DateInput type="date-time" name="accidentDateMin" label="Başlangıç" />
              </Grid>
              <Grid item xs={6} md={6} sm={6} lg={6}>
                <DateInput type="date-time" name="accidentDateMax" label="Bitiş" />
              </Grid>
            </Grid>
            <Grid item xs={12} md={12} sm={12}>
              <TextField name="accidentDescription" label="Kaza Tanımı" rows={3} />
            </Grid>
          </Grid>
          <footer className="px-2 flex-justify">
            <Button className="mr-1" fullWidth type="button" variant="contained" onClick={clear}>
              Temizle
            </Button>
            <Button fullWidth type="submit" variant="contained" color="secondary">
              Filtrele
            </Button>
          </footer>
        </Form>
      </Drawer>
    </>
  );
}
